import { useEffect } from "react";

interface PageMeta {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  jsonLd?: Record<string, unknown>;
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function usePageMeta({
  title,
  description,
  keywords,
  canonicalPath,
  jsonLd,
}: PageMeta) {
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    document.title = title;
    setMeta("name", "description", description);
    if (keywords) setMeta("name", "keywords", keywords);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    const base = `${window.location.origin}${__BASE_PATH__ || "/"}`.replace(/\/$/, "");
    link.setAttribute("href", `${base}${canonicalPath ?? ""}`);

    const existing = document.getElementById("page-jsonld");
    if (existing) existing.remove();
    if (jsonLdStr) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "page-jsonld";
      script.textContent = jsonLdStr;
      document.head.appendChild(script);
    }

    return () => {
      const cleanup = document.getElementById("page-jsonld");
      if (cleanup) cleanup.remove();
    };
  }, [title, description, keywords, canonicalPath, jsonLdStr]);
}